import React, { useCallback, useContext, useEffect, useRef } from 'react'
import { Formik, Form, Field, useFormikContext } from 'formik'
import { useLocation } from '@reach/router'
import { Grid, Cell } from 'react-foundation'
import * as Yup from 'yup'
import UserContext, { Roles } from '../../../UserContext'
import { ServicesFilter } from '../types'
import MemberSearchSelect from '../../Common/Input/MemberSearchSelect'
import UserSearchSelect from '../../Common/Input/UserSearchSelect'
import { DatePickerFormGroup, LookupCheckboxFieldGroup, FocusOnFirstError } from '../../Common/Input/FormFields'
import CountySearchSelect from '../../Common/Input/CountySearchSelect'
import { SaveEvent, FilterChangeEvent, SelectEvent, TrackedButton } from '../../Common/AnalyticsEvents'
import ClearButton from '../../Common/ClearButton'
import StatusSelect from '../../Common/Input/StatusSelect'
import { ServiceStatus } from '../../../models/enums'

const FilterAnalyticsWatcher: React.FC = () => {
    const user = useContext(UserContext)
    const { pathname } = useLocation()
    const { values } = useFormikContext<ServicesFilter>()
    const prevValuesRef = useRef(values)

    useEffect(() => {
        const prev = prevValuesRef.current
        prevValuesRef.current = values

        if (values.authorizationID !== prev.authorizationID && values.authorizationID && !prev.authorizationID)
            SaveEvent(SelectEvent(user, pathname, 'Filter_AuthorizationID_Entered'))

        if (values.status !== prev.status)
            SaveEvent(SelectEvent(user, pathname, `Filter_Status_${values.status ?? 'None'}`))

        if (values.serviceRequestMemberID !== prev.serviceRequestMemberID && values.serviceRequestMemberID)
            SaveEvent(SelectEvent(user, pathname, 'Filter_Member_Selected'))

        if (values.createdByUserID !== prev.createdByUserID && values.createdByUserID)
            SaveEvent(SelectEvent(user, pathname, 'Filter_CaseManager_Selected'))

        if (JSON.stringify(values.counties) !== JSON.stringify(prev.counties))
            SaveEvent(SelectEvent(user, pathname, `Filter_County_${(values.counties ?? []).join('_') || 'None'}`))

        if (JSON.stringify(values.specialties) !== JSON.stringify(prev.specialties))
            SaveEvent(SelectEvent(user, pathname, `Filter_Specialty_${(values.specialties ?? []).join('_') || 'None'}`))

        if (values.startDate !== prev.startDate && values.startDate)
            SaveEvent(SelectEvent(user, pathname, 'Filter_StartDate_Selected'))

        if (values.endDate !== prev.endDate && values.endDate)
            SaveEvent(SelectEvent(user, pathname, 'Filter_EndDate_Selected'))
    }, [values, user, pathname])

    return null
}

type FiltersProps = {
    filters: ServicesFilter
    onFilter: (filters: ServicesFilter) => void
    setActiveFilterTag: (tag: string) => void;
}

const Filters: React.FC<FiltersProps> = ({ onFilter, filters, setActiveFilterTag }) => {
    const userContext = useContext(UserContext)

    const validationScheme = Yup.object({
        status: Yup.string(),
        createdBy: Yup.string()
    })

    const getStatusOptions = useCallback(()=>{
        if(!filters.filterGroup)
            return undefined
        switch(filters.filterGroup)
        {
            case 'Open':
            case 'Responses':
            case 'Awarded':
                return ['Active', 'Inactive', 'InactiveCompleted', 'InactiveRescinded', 'InactiveDenied'] as ServiceStatus[]
        }
        return ['Active', 'Inactive', 'InactiveCompleted', 'InactiveRescinded', 'InactiveDenied'] as ServiceStatus[]
    }, [filters.filterGroup])

    return (
        <Formik
            initialValues={filters}
            validationSchema={validationScheme}
            onSubmit={(values) => {
                let { pageSize, filterGroup } = filters
                if (!userContext.hasAnyRole(Roles.Provider)) {
                    filterGroup = 'All';
                }
                SaveEvent(FilterChangeEvent(userContext, 'Service Requests', values.counties, values.specialties))
                const newFilter = { ...values, pageSize, filterGroup, rowIndex: 0 }
                if(values.status !== 'Draft')
                    newFilter.lastNonDraftStatus = values.status
                onFilter(newFilter)
                !userContext.hasAnyRole(Roles.Provider) && setActiveFilterTag('All')
            }}>
            <Form className='filters'>
                <FilterAnalyticsWatcher />
                <Grid>
                    <Cell small={12} large={12}>
                        <h4><i className='fas fa-clipboard-list'></i>Find By ID</h4>
                        <Field type="text" name="authorizationID" placeholder="Authorization ID"></Field>
                    </Cell>
                    {!userContext.hasAnyRole(Roles.Provider) &&
                        <>
                            <Cell small={12} large={12}>
                                <h4><i className='fas fa-search'></i>Search By</h4>
                            </Cell>
                            <Cell small={5} large={5}>
                                <MemberSearchSelect name='serviceRequestMemberID' placeholder='Member ID or Name' className="bigMenu" />
                            </Cell>
                            <Cell small={3} large={3}>
                                <StatusSelect noLabel name='status' placeholder='Status' optionsList={getStatusOptions()} readableSeperator={' - '} />
                            </Cell>
                            <Cell small={4} large={4}>
                                <UserSearchSelect name='createdByUserID' placeholder='Case Manager' />
                            </Cell>
                        </>}
                    <div className="est"></div>
                    <Cell small={12} large={12}>
                        <h4><i className='far fa-map-marker-alt'></i>Region</h4>
                    </Cell>
                    
                    <CountySearchSelect name='counties' stateName='countiesState' title='Counties' hideLabel={true} />
                    
                    <Cell small={12} large={12} className='two-column'>
                        <h4><i className='fas fa-hammer'></i>Type</h4>
                        <LookupCheckboxFieldGroup lookupKey='SpecialtyServicesCategories' name='specialties' noLabel />
                    </Cell>

                    <Cell small={12} large={12}>
                        <h4><i className='fas fa-calendar'></i>Posting ends between</h4>
                    </Cell>
                    <Cell small={4} large={3}>
                        <DatePickerFormGroup noLabel name="startDate" placeholderText="mm/dd/yyyy" />
                    </Cell>
                    <Cell small={2} large={1} className='text-center'>
                        and
                    </Cell>
                    <Cell small={4} large={3}>
                        <DatePickerFormGroup noLabel name="endDate" placeholderText="mm/dd/yyyy" />
                    </Cell>
                </Grid>
                <br />

                <div style={{ display: 'flex', flexDirection: 'row-reverse', gap: 15 }}>
                    <TrackedButton event='FilterSubmit'><input className='button filter' type='submit' value='Filter' /></TrackedButton>
                    <TrackedButton event='FilterClear'><ClearButton resetValue={{ authorizationID: '', status: 'Active', counties: [], specialties: [], sort: [['startDate', 'desc']] }} /></TrackedButton>
                </div>

                <FocusOnFirstError />
            </Form>
        </Formik>
    )
}

export default Filters
